import { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';

const GymContext = createContext(null);

export const GymProvider = ({ children }) => {
  const { user } = useAuth();
  const [userGyms, setUserGyms] = useState([]);
  const [selectedGym, setSelectedGym] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [recentGyms, setRecentGyms] = useState([]);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

  // Load recent gyms from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem('recentGymIds');
    if (stored) {
      try {
        setRecentGyms(JSON.parse(stored));
      } catch (e) {
        console.error('Error loading recent gyms:', e);
      }
    }
  }, []);

  // Load user's gyms when user changes
  useEffect(() => {
    if (user) {
      loadUserGyms();
    } else {
      setUserGyms([]);
      setSelectedGym(null);
      setIsLoading(false);
    }
  }, [user]);

  const loadUserGyms = async () => {
    try {
      setIsLoading(true);
      const response = await fetch(`${API_URL}/api/users/gyms`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) throw new Error('Failed to load gyms');

      const gyms = await response.json();
      setUserGyms(gyms);

      // Set primary gym as selected by default (or first gym if no primary)
      const primaryGym = gyms.find(g => g.isPrimary) || gyms[0];
      if (primaryGym) {
        setSelectedGym(primaryGym);
      }
    } catch (error) {
      console.error('Error loading user gyms:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const selectGym = (gym) => {
    setSelectedGym(gym);
    // Store in localStorage for persistence across page refreshes
    localStorage.setItem('selectedGymId', gym.gymId);

    // Update recent gyms (keep last 5, most recent first)
    const newRecent = [gym.gymId, ...recentGyms.filter(id => id !== gym.gymId)].slice(0, 5);
    setRecentGyms(newRecent);
    localStorage.setItem('recentGymIds', JSON.stringify(newRecent));
  };

  // Select gym for one-time booking (doesn't add to user's gym list)
  const selectGymOnce = (gym) => {
    // Convert to userGym format if it's a raw gym object
    const gymToSelect = gym.gymId ? gym : {
      gymId: gym.id,
      name: gym.name,
      city: gym.city,
      state: gym.state,
      isPrimary: false
    };

    setSelectedGym(gymToSelect);
    // Store in localStorage for persistence across page refreshes
    localStorage.setItem('selectedGymId', gymToSelect.gymId);

    // Update recent gyms for one-time bookings too
    const newRecent = [gymToSelect.gymId, ...recentGyms.filter(id => id !== gymToSelect.gymId)].slice(0, 5);
    setRecentGyms(newRecent);
    localStorage.setItem('recentGymIds', JSON.stringify(newRecent));
  };

  // Add a gym to user's list
  const addGym = async (gymId) => {
    try {
      const response = await fetch(`${API_URL}/api/users/gyms`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ gymId })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to add gym');
      }

      await loadUserGyms();
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  // Remove a gym from user's list
  const removeGym = async (userGymId) => {
    try {
      const response = await fetch(`${API_URL}/api/users/gyms/${userGymId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to remove gym');
      }

      await loadUserGyms();
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  // Set a gym as primary
  const setPrimaryGym = async (userGymId) => {
    try {
      const response = await fetch(`${API_URL}/api/users/gyms/${userGymId}/set-primary`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to set primary gym');
      }

      await loadUserGyms();
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const value = {
    userGyms,
    selectedGym,
    isLoading,
    recentGyms,
    selectGym,
    selectGymOnce,
    addGym,
    removeGym,
    setPrimaryGym,
    refreshGyms: loadUserGyms
  };

  return (
    <GymContext.Provider value={value}>
      {children}
    </GymContext.Provider>
  );
};

export const useGym = () => {
  const context = useContext(GymContext);
  if (!context) {
    throw new Error('useGym must be used within a GymProvider');
  }
  return context;
};

export { GymContext };
